import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { readSeizedFinanceRecords } from '../../lib/workspaceStore';
import { inRange } from '../../lib/dateFilter';
import { useDashboardDetailDates } from '../../lib/useDashboardDetailDates';
import type { ExportRow } from '../../lib/exportReports';
import { DashboardDetailShell, DashboardMetricCard, type DashboardDetailDateProps } from './DashboardDetailShell';

interface SeizedHPsProps extends DashboardDetailDateProps {
  onClose: () => void;
}

export function SeizedHPs({ onClose, initialDateRange }: SeizedHPsProps) {
  const dr = useDashboardDetailDates(initialDateRange);

  const seized = useMemo(() => {
    const all = readSeizedFinanceRecords();
    return all.filter((r) => {
      const d = r.data;
      const seizedDate = String(d.seizedDate ?? '');
      return seizedDate ? inRange(seizedDate, dr.range) : inRange(r.updatedAt, dr.range);
    });
  }, [dr.range]);

  const exportRows: ExportRow[] = useMemo(
    () =>
      seized.map((r) => {
        const d = r.data;
        return {
          Customer: String(d.customerName ?? '—'),
          Mobile: String(d.mobileNumber ?? '—'),
          Registration: String(d.regNo ?? d.haNumber ?? '—'),
          Line: String(d.line ?? '—'),
          SeizedDate: String(d.seizedDate ?? '—'),
          Reason: String(d.seizedReason ?? '—'),
        };
      }),
    [seized]
  );

  return (
    <DashboardDetailShell
      onClose={onClose}
      title="Seized hire purchases"
      subtitle="Vehicles marked seized in the selected period."
      icon={AlertTriangle}
      iconClassName="text-rose-600"
      exportTitle="Seized Vehicles Report"
      exportFilename="seized-vehicles.xls"
      exportRows={exportRows}
      dateRange={dr.range}
      datePreset={dr.preset}
      onDateStart={dr.setStart}
      onDateEnd={dr.setEnd}
      onDatePreset={dr.setPreset}
      onDateClear={dr.clear}
    >
      <DashboardMetricCard label="Seized units" value={seized.length} accent />

      {seized.length === 0 ? (
        <div className="app-card border-dashed border-amber-200 bg-amber-50/80 shadow-sm">
          <div className="app-card-inner py-10 text-center text-sm text-amber-950">
            No seized vehicles in this date range.
          </div>
        </div>
      ) : (
        <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
          <div className="border-b border-zinc-200 bg-zinc-50/90 px-4 py-3">
            <h2 className="text-sm font-semibold text-zinc-900">Seized facilities</h2>
            <p className="text-xs text-zinc-500">{seized.length} record{seized.length === 1 ? '' : 's'}</p>
          </div>
          <div className="app-table-wrap border-0">
            <table className="app-table min-w-full">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Mobile</th>
                  <th>Registration</th>
                  <th>Line</th>
                  <th>Seized date</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {seized.map((r) => {
                  const d = r.data;
                  return (
                    <tr key={r.id} className="transition-colors hover:bg-rose-50/40">
                      <td className="font-medium text-zinc-900">{String(d.customerName ?? '—')}</td>
                      <td>{String(d.mobileNumber ?? '—')}</td>
                      <td>{String(d.regNo ?? d.haNumber ?? '—')}</td>
                      <td>{String(d.line ?? '—')}</td>
                      <td>{String(d.seizedDate ?? '—')}</td>
                      <td className="max-w-xs whitespace-normal text-sm text-rose-900">{String(d.seizedReason ?? '—')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardDetailShell>
  );
}
